import React, { useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

interface DateFilterProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onApply?: (fromDate: string, toDate: string) => void;
  onClearAll?: () => void;
  className?: string;
  variant?: 'filled' | 'outlined' | 'standard';
}

const DateFilter: React.FC<DateFilterProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onApply,
  onClearAll,
  className,
  variant = 'filled',
}) => {
  const isRangeInvalid = Boolean(
    fromDate && toDate && moment(fromDate, 'YYYY-MM-DD').isAfter(moment(toDate, 'YYYY-MM-DD'))
  );
  const hasBothDates = Boolean(fromDate && toDate);
  const isApplyDisabled = !hasBothDates || isRangeInvalid;
  const isClearDisabled = !hasBothDates;

  const fromInputRef = useRef<HTMLInputElement | null>(null);
  const toInputRef = useRef<HTMLInputElement | null>(null);

  const openNativePicker = (ref: React.RefObject<HTMLInputElement>) => {
    const node = ref.current as any;
    if (node && typeof node.showPicker === 'function') {
      try {
        node.showPicker();
        return;
      } catch (e) {
        // ignore and fallback to focus
      }
    }
    if (ref.current) {
      ref.current.focus();
      ref.current.click();
    }
  };

  return (
    <div className={className}>
      <div className="date-filter-grid">
          <TextField
            type="date"
            variant={variant}
            size="small"
            margin="dense"
            value={fromDate}
            onChange={(e) => onFromDateChange(e.target.value)}
            className="date-filter-field date-filter-field-from"
            inputRef={fromInputRef}
            onClick={() => openNativePicker(fromInputRef)}
            inputProps={{ max: toDate || undefined, 'aria-label': 'From date' }}
            error={isRangeInvalid}
          />
        <div className="date-filter-row">
          <TextField
            type="date"
            variant={variant}
            size="small"
            margin="dense"
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
            className="date-filter-field"
            inputRef={toInputRef}
            onClick={() => fromDate && openNativePicker(toInputRef)}
            inputProps={{ min: fromDate || undefined, 'aria-label': 'To date' }}
            disabled={!fromDate}
            error={isRangeInvalid}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onApply) onApply(fromDate, toDate);
            }}
          />
          <div className="date-filter-actions">
            <IconButton
              aria-label="apply date filter"
              className="date-filter-apply"
              size="small"
              onClick={() => onApply && onApply(fromDate, toDate)}
              disabled={isApplyDisabled}
            >
              <ArrowForwardIcon />
            </IconButton>
            <IconButton
              aria-label="clear dates"
              className="date-filter-clear-all"
              size="small"
              onClick={() => {
                onFromDateChange('');
                onToDateChange('');
                if (onClearAll) onClearAll();
              }}
              disabled={isClearDisabled}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateFilter;


